import type { KeyboardEvent } from 'react';

const FOCUSABLE_SELECTOR =
    'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Focusable elements inside a container, in document order (for header toolbar-style arrow navigation).
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1 && !el.hasAttribute('data-skip-header-arrow-nav')
    );
}

function isTextEditingElement(el: HTMLElement): boolean {
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        return true;
    }
    return el.isContentEditable;
}

/**
 * Arrow Left/Right (and Home/End) move focus between header controls. Skips when focus is in a text field
 * so caret keys keep working. Honors `dir="rtl"` by mirroring horizontal arrows.
 */
export function handleHeaderArrowNavigation(header: HTMLElement, e: KeyboardEvent<HTMLElement>): void {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') {
        return;
    }

    const active = document.activeElement as HTMLElement | null;
    if (!active || !header.contains(active)) {
        return;
    }

    if (isTextEditingElement(active)) {
        return;
    }

    const items = getFocusableElements(header);
    if (items.length < 2) {
        return;
    }

    const idx = items.indexOf(active);
    if (idx < 0) {
        return;
    }

    const isRtl =
        typeof document !== 'undefined' &&
        (getComputedStyle(header).direction === 'rtl' || document.documentElement.dir === 'rtl');

    let nextIdx = idx;
    if (e.key === 'Home') {
        nextIdx = 0;
    } else if (e.key === 'End') {
        nextIdx = items.length - 1;
    } else if (e.key === 'ArrowRight') {
        nextIdx = isRtl ? (idx - 1 + items.length) % items.length : (idx + 1) % items.length;
    } else {
        // ArrowLeft
        nextIdx = isRtl ? (idx + 1) % items.length : (idx - 1 + items.length) % items.length;
    }

    if (nextIdx === idx) {
        return;
    }

    e.preventDefault();
    e.stopPropagation();
    items[nextIdx]?.focus();
}
